import { parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import type { SessionNoteSummary } from '@/api/sessionNotes/sessionNotesApi.types';
import { Badge } from '@/components/ui/badge';
import { Icon } from '@/components/common/Icon';
import { formatDateToDayMonthYear } from '@/utils/dateFormatting';

interface SessionNoteRowProps {
  note: SessionNoteSummary;
  onOpen: (noteId: string) => void;
}

export function SessionNoteRow({ note, onOpen }: SessionNoteRowProps) {
  const { t } = useTranslation('patients');
  const isCompleted = note.status === 'completed';
  const displayDate = note.visitDate === null ? note.createdAt : note.visitDate;

  return (
    <button
      type="button"
      onClick={function open() {
        onOpen(note.id);
      }}
      className={
        'w-full flex flex-wrap items-start gap-3 p-3 border rounded-xl text-left hover:border-zooprimary transition-colors ' +
        (isCompleted
          ? 'border-zooborder'
          : 'border-zooborder border-l-4 border-l-zooerrortext')
      }
    >
      <span className="w-9 h-9 rounded-lg bg-zoovpalelavender text-zooprimary flex items-center justify-center shrink-0">
        <Icon name="FileText" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-zooindigo">
            {formatDateToDayMonthYear(parseISO(displayDate))}
          </span>
          <Badge tone={isCompleted ? 'success' : 'danger'} size="sm">
            {isCompleted ? t('sessionNote.statusCompleted') : t('sessionNote.statusDraft')}
          </Badge>
          {note.treatmentCount === 0 ? null : (
            <Badge tone="muted" size="sm">
              {t('sessionNotesList.treatmentCount', { count: note.treatmentCount })}
            </Badge>
          )}
        </div>
        {note.authorName === null ? null : (
          <p className="text-xs text-zoomuted mt-1 flex items-center gap-1">
            <Icon name="User" />
            {note.authorName}
          </p>
        )}
        {note.preview === null ? null : (
          <p className="text-sm text-zooindigo mt-1.5 line-clamp-2">{note.preview}</p>
        )}
      </div>
      <Icon name="ChevronRight" className="text-zoomuted shrink-0 self-center" />
    </button>
  );
}
